import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import { styled } from '@uhg-abyss/web/tools/styled';
import { ProviderContext } from '../Context';

const StyledDropdownOverlay = styled('div', {
  position: 'fixed',
  zIndex: 1000,
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: '$black',
  transition: 'opacity $duration ms',
  '&.entering, &.entered': {
    opacity: 0.5,
  },
  '&.exiting, &.exited': {
    opacity: 0,
  },
});

const StyledDropdownSibling = styled('div', {
  position: 'relative',
  zIndex: 1001,
  '&.disableNav': {
    zIndex: 0,
  },
});

export const DropdownOverlay = ({
  children,
  model,
  className,
  durationEnter,
  durationExit,
  siblingClass,
  ...props
}) => {
  const { dropdownState, setDropdownState } = useContext(ProviderContext);

  if (!dropdownState) {
    throw new Error(
      '<Dropdown.Overlay> needs to be wrapped in a <Dropdown.Provider>'
    );
  }

  const isActive = dropdownState.model && dropdownState.model.startsWith(model);

  const handleClick = () => {
    setDropdownState({});
  };

  return (
    <React.Fragment>
      {children && (
        <StyledDropdownSibling className={siblingClass}>
          {children}
        </StyledDropdownSibling>
      )}
      <CSSTransition
        in={isActive}
        timeout={{ enter: durationEnter, exit: durationExit }}
        mountOnEnter
        unmountOnExit
        classNames={{
          enter: 'entering',
          enterActive: 'entered',
          exitActive: 'exiting',
          exit: 'exited',
        }}
      >
        {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus,jsx-a11y/click-events-have-key-events */}
        {(state) => {
          return (
            <StyledDropdownOverlay
              {...props}
              role="button"
              onClick={handleClick}
              className={className}
              style={{
                '--transition-duration': `${
                  state === 'entering' ? durationEnter : durationExit
                }ms`,
              }}
            />
          );
        }}
      </CSSTransition>
    </React.Fragment>
  );
};

DropdownOverlay.propTypes = {
  children: PropTypes.node.isRequired,
  model: PropTypes.string.isRequired,
  className: PropTypes.string,
  durationEnter: PropTypes.number,
  durationExit: PropTypes.number,
  siblingClass: PropTypes.string,
};

DropdownOverlay.defaultProps = {
  className: null,
  durationEnter: 200,
  durationExit: 600,
  siblingClass: null,
};
